import React, { useState, useEffect } from 'react';
import { X, User, Mail, Phone, Building2, Shield, Briefcase, Users } from 'lucide-react';
import toast from 'react-hot-toast';

const UserFormModal = ({ user, onClose, onSuccess, backendUrl }) => {
  const isEditMode = !!user;
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    roles: ['recruiter'],
    is_local_user: true,
    department: '',
    phone: '',
    industry: '',
    is_active: true
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Populate form when editing
  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        full_name: user.full_name || '',
        password: '', // Don't pre-fill password
        roles: user.roles || (user.role ? [user.role] : ['recruiter']),
        is_local_user: user.is_local_user ?? true,
        department: user.department || '',
        phone: user.phone || '',
        industry: user.industry || '',
        is_active: user.is_active ?? true
      });
    }
  }, [user]);

  const roleOptions = [
    { value: 'recruiter', label: 'Recruiter', description: 'Standard recruiter role' },
    { value: 'lead_recruiter', label: 'Lead Recruiter', description: 'Team lead with recruiter management' },
    { value: 'recruiter_supervisor', label: 'Recruiter Supervisor', description: 'Oversees recruiting teams and operations' },
    { value: 'account_manager', label: 'Account Manager', description: 'Manages customer relationships' },
    { value: 'account_exec', label: 'Account Exec', description: 'Account executive role' },
    { value: 'admin', label: 'Administrator', description: 'System administrator' },
    { value: 'hr', label: 'HR Manager', description: 'Human resources role' }
  ];

  const industryOptions = [
    'Technology',
    'Healthcare',
    'Finance',
    'Retail',
    'Manufacturing',
    'Education',
    'Consulting',
    'Real Estate',
    'Hospitality',
    'Other'
  ];

  const validate = () => {
    const newErrors = {};
    
    if (!formData.username || formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }
    
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Valid email is required';
    }
    
    if (!isEditMode && formData.is_local_user && !formData.password) {
      newErrors.password = 'Password is required for local users';
    }
    
    if (!isEditMode && formData.password && formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!formData.full_name) {
      newErrors.full_name = 'Full name is required';
    }

    // Industry required for recruiters, leads, and recruiter supervisors
    const hasRecruitingRole = formData.roles.some(role => 
      ['recruiter', 'lead_recruiter', 'recruiter_supervisor'].includes(role)
    );
    if (hasRecruitingRole && !formData.industry) {
      newErrors.industry = 'Industry specialization is required for recruiting roles';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setSubmitting(true);
    try {
      const url = isEditMode 
        ? `${backendUrl}/api/users/${user.id}`
        : `${backendUrl}/api/users`;
      
      const method = isEditMode ? 'PUT' : 'POST';
      
      // Prepare data - remove password if empty in edit mode
      const dataToSend = {...formData};
      if (isEditMode && !dataToSend.password) {
        delete dataToSend.password;
      }
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(dataToSend)
      });

      const result = await response.json();

      if (response.ok) {
        toast.success(result.message || `User ${isEditMode ? 'updated' : 'created'} successfully!`);
        onSuccess();
        onClose(); // Close modal after success
      } else {
        // Handle validation errors properly
        let errorMessage = `Failed to ${isEditMode ? 'update' : 'create'} user`;
        
        if (result.detail) {
          if (typeof result.detail === 'string') {
            errorMessage = result.detail;
          } else if (Array.isArray(result.detail)) {
            // Pydantic validation errors - extract messages
            errorMessage = result.detail.map(err => err.msg || JSON.stringify(err)).join(', ');
          } else if (typeof result.detail === 'object') {
            errorMessage = JSON.stringify(result.detail);
          }
        }
        
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error('Error saving user: ' + (error.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  const shouldShowIndustry = formData.roles.some(role => 
    ['recruiter', 'lead_recruiter', 'recruiter_supervisor'].includes(role)
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditMode ? 'Edit User' : 'Add New User'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Username <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  disabled={isEditMode}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.username ? 'border-red-500' : 'border-gray-300'
                  } ${isEditMode ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder="e.g., john.smith"
                />
                {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.full_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., John Smith"
                />
                {errors.full_name && <p className="text-red-500 text-xs mt-1">{errors.full_name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="john.smith@company.com"
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="+1-555-0123"
                />
              </div>
            </div>
          </div>

          {/* Role & Access */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Role & Access</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Roles <span className="text-red-500">*</span>
                </label>
                <div className="space-y-2">
                  {roleOptions.map(option => (
                    <label 
                      key={option.value}
                      className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={formData.roles.includes(option.value)}
                        onChange={(e) => {
                          const newRoles = e.target.checked
                            ? [...formData.roles, option.value]
                            : formData.roles.filter(r => r !== option.value);
                          setFormData({...formData, roles: newRoles});
                        }}
                        className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">{option.label}</div>
                        <div className="text-xs text-gray-500">{option.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Select one or more roles for this user
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Department
                </label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => setFormData({...formData, department: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Recruiting, Sales"
                />
              </div>

              {/* Industry - Only for Recruiters and Leads */}
              {shouldShowIndustry && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Industry Specialization <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.industry}
                    onChange={(e) => setFormData({...formData, industry: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.industry ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select Industry...</option>
                    {industryOptions.map(industry => (
                      <option key={industry} value={industry}>
                        {industry}
                      </option>
                    ))}
                  </select>
                  {errors.industry && <p className="text-red-500 text-xs mt-1">{errors.industry}</p>}
                  <p className="text-xs text-gray-500 mt-1">
                    Primary industry this recruiter focuses on
                  </p>
                </div>
              )}

              <div className="md:col-span-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_local_user}
                    onChange={(e) => setFormData({...formData, is_local_user: e.target.checked})}
                    disabled={isEditMode}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Local User (uses password authentication)</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  {isEditMode ? 'User type cannot be changed after creation' : 'Uncheck for Microsoft/Entra ID users'}
                </p>
              </div>

              {formData.is_local_user && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password {!isEditMode && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.password ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder={isEditMode ? 'Leave blank to keep current password' : 'Minimum 6 characters'}
                  />
                  {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                  {isEditMode && (
                    <p className="text-xs text-gray-500 mt-1">
                      Leave blank to keep the current password
                    </p>
                  )}
                </div>
              )}

              <div className="md:col-span-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Active User</span>
                </label>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  Inactive users cannot log in to the system
                </p>
              </div>
            </div>
          </div>

          {/* Role-Specific Fields Summary */}
          {formData.roles.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">
                Combined Role Permissions ({formData.roles.length} role{formData.roles.length > 1 ? 's' : ''} selected)
              </h4>
              <div className="text-xs text-blue-800 space-y-2">
                {formData.roles.map(role => (
                  <div key={role} className="border-l-2 border-blue-300 pl-3">
                    <div className="font-semibold mb-1">{roleOptions.find(r => r.value === role)?.label}</div>
                    {role === 'recruiter' && (
                      <>
                        <p>• Manage own candidates and submissions</p>
                        <p>• View jobs and customer information</p>
                        <p>• Create and update candidate profiles</p>
                      </>
                    )}
                    {role === 'lead_recruiter' && (
                      <>
                        <p>• All recruiter permissions</p>
                        <p>• Manage team members</p>
                        <p>• View team performance metrics</p>
                        <p>• Approve submissions</p>
                      </>
                    )}
                    {role === 'recruiter_supervisor' && (
                      <>
                        <p>• All lead recruiter permissions</p>
                        <p>• Oversee multiple recruiting teams</p>
                        <p>• Strategic recruiting initiatives</p>
                        <p>• Company-wide recruiting metrics</p>
                      </>
                    )}
                    {role === 'account_manager' && (
                      <>
                        <p>• Manage customer relationships</p>
                        <p>• Create and manage job requisitions</p>
                        <p>• View submissions and candidates</p>
                      </>
                    )}
                    {role === 'account_exec' && (
                      <>
                        <p>• Account executive responsibilities</p>
                        <p>• Client communication and coordination</p>
                        <p>• Sales and business development</p>
                      </>
                    )}
                    {role === 'admin' && (
                      <>
                        <p>• Full system access</p>
                        <p>• User and team management</p>
                        <p>• System configuration</p>
                        <p>• Audit logs and analytics</p>
                      </>
                    )}
                    {role === 'hr' && (
                      <>
                        <p>• HR management functions</p>
                        <p>• Employee data access</p>
                        <p>• Compliance and reporting</p>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update User' : 'Create User')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserFormModal;
